export const CARD_WRAP: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  // Figma "Full screen / Chat Layout" QA (Monday 12819150245): 30px between one message and the
  // next — was 22.
  marginBottom: 30,
};

export const SPEAKER_HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 6,
};

export const SPEAKER_AVATAR: React.CSSProperties = {
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%',
  color: 'var(--sym-ink)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.6875rem',
  fontWeight: 800,
  flexShrink: 0,
};

// Name · role label above a brief turn — both 14/18, the name Medium and the role Regular
// (Figma "Morning Brief" 14701-105160 / 14701-105164). Name in primary ink, role in the secondary.
export const SPEAKER_NAME: React.CSSProperties = { fontSize: '0.875rem', lineHeight: '1.125rem', fontWeight: 'var(--sym-weight-medium)', color: 'rgba(var(--ink-rgb),0.9)' };
export const SPEAKER_TITLE: React.CSSProperties = { fontSize: '0.875rem', lineHeight: '1.125rem', fontWeight: 400, color: 'rgba(var(--ink-rgb),0.5)' };

// Size to content (capped) so a short message stays on one line. A `%` max-width
// here would resolve against the shrink-to-fit wrapper and force even short text
// to wrap. White sunken-surface bubble, no accent gradient, no shadow (Symphony
// chat spec — Figma "Surface/Sunken"); `--sym-bg-elevated` keeps it dark-ready.
export const USER_BUBBLE: React.CSSProperties = {
  width: 'fit-content',
  // min(): 600 is the wide-viewport cap, 100% keeps the bubble inside a narrow panel even when
  // an unwrappable child (the reply quote's nowrap preview) would otherwise force it wider.
  maxWidth: 'min(600px, 100%)',
  padding: 16,
  borderRadius: 'var(--sym-radius-chat-bubble)',
  fontSize: '1rem',
  lineHeight: 1.5,
  color: 'var(--sym-ink)',
  background: 'var(--sym-bg-elevated)',
  border: 'none',
  boxShadow: 'none',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

export const USER_BUBBLE_VOICE: React.CSSProperties = {
  ...USER_BUBBLE,
  padding: 20,
  background: 'rgba(var(--ds-surface-sunken-rgb), 0.7)',
};

// A sub-agent's brief turn sits in a bordered bubble — white/elevated fill, the agent's own identity
// colour as a 1px border (colour set inline per agent, native draws `colorForAgent(id).opacity(0.5)`).
// Left-aligned, hugging its content up to the reading cap (Figma "Morning Brief" 6577-52426/-52447).
export const AGENT_BUBBLE: React.CSSProperties = {
  width: 'fit-content',
  // 672 is the wide-chat cap (Figma "Morning Brief" 14535-166178); 100% keeps the bubble inside the
  // narrow docked panel even when an unwrappable child would otherwise force it wider.
  maxWidth: 'min(672px, 100%)',
  // No global border-box reset here, so keep padding + border inside the max-width — otherwise a
  // full-width bubble overflows the pane by its 16px padding + 1px border (reported overflow).
  boxSizing: 'border-box',
  padding: 16,
  borderRadius: 'var(--sym-radius-chat-bubble)',
  fontSize: '1rem',
  lineHeight: 1.5,
  color: 'rgba(var(--ink-rgb),0.88)',
  background: 'var(--sym-bg-elevated)',
  border: '1px solid transparent',
  wordBreak: 'break-word',
};

// Assistant prose flows on the surface with no bubble (mobile pattern); the
// reading column caps line length on wide viewports.
//
// ⚠️ `flex: 1` is what stops a HEBREW reply flickering, and it is not cosmetic. This div is an item of
// MessageRow's flex ROW, so without a flex basis its width is its CONTENT width — it grows with the
// longest line as the reply streams in. In LTR that is invisible: every line is aligned to the box's
// inline-start edge, which does not move. In RTL every line is aligned to the box's inline-END edge,
// which is `start + contentWidth` — so each delta that extends the longest line drags EVERY ALREADY
// SETTLED LINE of the message sideways with it.
//
// Measured in Chrome on this exact markup, streaming one Hebrew list item: the settled first line's
// aligned edge travelled 416px in 7 jumps of 44-68px. The same run in English: 0px, in both cases,
// which is precisely why this was reported as "only in Hebrew". With `flex: 1` the box is the reading
// column at every point in the stream, and the settled text does not move at all.
//
// `minWidth: 0` goes with it — a flex item's default `min-width: auto` refuses to shrink below its
// content, which would push a long unbreakable token out of the column instead of wrapping it.
export const ASSISTANT_BODY: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  maxWidth: 720,
  paddingBlock: 4,
  paddingInlineEnd: 4,
  paddingInlineStart: 0,
  fontSize: '1rem',
  lineHeight: 1.5,
  color: 'rgba(var(--ink-rgb),0.88)',
  wordBreak: 'break-word',
};

export const TIME_BASE: React.CSSProperties = {
  marginTop: 6,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  letterSpacing: '0.01em',
  textAlign: 'end',
};
